import { BusinessIdea } from './businessIdeaTypes';
import { BusinessPlan, ExtendedBusinessPlan } from '../types/businessPlan';
import { ENV } from '../config/env';
import { supabase, supabaseHelper } from '../lib/supabase';
import {
  saveBusinessPlanToSupabase,
  getBusinessPlanFromSupabase,
  getBusinessPlanByIdeaIdFromSupabase,
  getUserBusinessPlansFromSupabase,
  deleteBusinessPlanFromSupabase,
  deleteAllBusinessPlansFromSupabase
} from './supabase/businessPlanService';

// Business Idea Functions - Using Supabase
export const getSavedBusinessIdeas = async (): Promise<BusinessIdea[]> => {
  try {
    const ideas = await supabaseHelper.getIdeas();
    
    // Map Supabase field names to our BusinessIdea interface
    return ideas.map(idea => ({
      id: idea.id,
      title: idea.title || '',
      description: idea.description || '',
      potentialMarket: idea.target_market || '',
      industry: idea.industry || '',
      skillsUtilized: [],
      createdAt: idea.created_at,
      // Add any other fields needed with defaults
    }));
  } catch (error) {
    console.error('Error getting business ideas from Supabase:', error);
    return [];
  }
};

export const saveToSavedIdeas = async (idea: BusinessIdea): Promise<void> => {
  try {
    // Map our BusinessIdea interface to Supabase fields
    const supabaseIdea = {
      title: idea.title,
      description: idea.description,
      target_market: idea.potentialMarket,
      industry: idea.industry,
      // Add any other fields needed
    };
    
    await supabaseHelper.createIdea(supabaseIdea);
  } catch (error) {
    console.error('Error saving business idea to Supabase:', error);
    throw error;
  }
};

export const deleteBusinessIdea = async (ideaId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('ideas')
      .delete()
      .eq('id', ideaId);
    
    if (error) throw error;
  } catch (error) {
    console.error('Error deleting business idea from Supabase:', error);
    throw error;
  }
};

// API request helper for business plans
async function makeRequest<T>(options: {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: any;
}): Promise<T> {
  const { endpoint, method = 'GET', data } = options;
  const url = `${ENV.API_URL}${endpoint}`;

  try {
    console.log(`Making ${method} request to ${url}`);
    
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    const responseText = await response.text();
    let responseData;
    
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response as JSON:', responseText);
      throw new Error(`API request failed: ${response.status} ${response.statusText} - ${responseText}`);
    }
    
    if (!response.ok) {
      console.error('API request failed:', responseData);
      throw new Error(`API request failed: ${response.status} ${response.statusText} - ${JSON.stringify(responseData)}`);
    }

    return responseData;
  } catch (error) {
    console.error('Request failed:', error);
    throw error;
  }
}

// Business Plan Functions - Using API or Supabase
export const getSavedBusinessPlans = async (): Promise<BusinessPlan[]> => {
  try {
    // Get plans from Supabase using the authenticated user
    return await getUserBusinessPlansFromSupabase();
  } catch (error) {
    console.error('Error getting business plans from Supabase, falling back to API:', error);
    // Fallback to API
    return makeRequest<BusinessPlan[]>({
      endpoint: '/api/business-plans',
    });
  }
};

export const saveBusinessPlan = async (businessPlan: Partial<ExtendedBusinessPlan>): Promise<ExtendedBusinessPlan> => {
  // Ensure required fields
  const planToSave: ExtendedBusinessPlan = {
    ...businessPlan as ExtendedBusinessPlan,
    id: businessPlan.id || crypto.randomUUID(),
    created_at: businessPlan.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
    version: businessPlan.version || 1
  };
  
  try {
    // Try to save to Supabase first
    // Check if this is from CustomBusinessPlanWizard by looking for additionalData
    const isCustomWizard = !!businessPlan.additionalData;
    return await saveBusinessPlanToSupabase(planToSave, isCustomWizard);
  } catch (error) {
    console.error('Error saving business plan to Supabase:', error);
    // Instead of falling back to API, return the local plan with a status indicating it couldn't be saved
    return {
      ...planToSave,
      status: 'draft',
      uiError: 'Failed to save to database'
    };
  }
};

export const getBusinessPlanById = async (ideaId: string): Promise<ExtendedBusinessPlan | null> => {
  try {
    console.log('Fetching business plan for idea ID:', ideaId);
    // Try to get from Supabase first
    return await getBusinessPlanByIdeaIdFromSupabase(ideaId);
  } catch (error) {
    console.error('Error fetching business plan by idea ID:', error);
    return null;
  }
};

export const deleteBusinessPlan = async (planId: string): Promise<void> => {
  try {
    // Delete from Supabase
    await deleteBusinessPlanFromSupabase(planId);
  } catch (error) {
    console.error('Error deleting business plan:', error);
    throw error;
  }
};

export const getLatestBusinessPlan = async (): Promise<ExtendedBusinessPlan | null> => {
  try {
    // Get plans from Supabase using the authenticated user
    const plans = await getUserBusinessPlansFromSupabase();
    return plans.length > 0 ? plans[0] : null;
  } catch (error) {
    console.error('Error getting latest business plan from Supabase, falling back to API:', error);
    // Fallback to API
    return makeRequest<ExtendedBusinessPlan | null>({
      endpoint: '/api/business-plans/latest',
    });
  }
};

// Export the Supabase functions directly
export { 
  saveBusinessPlanToSupabase, 
  getBusinessPlanFromSupabase, 
  getBusinessPlanByIdeaIdFromSupabase,
  getUserBusinessPlansFromSupabase,
  deleteBusinessPlanFromSupabase,
  deleteAllBusinessPlansFromSupabase
};
